import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";
import {
  BellRing,
  Lightbulb,
  Flame,
  FileText,
  ClipboardCheck,
  FileCheck2,
} from "lucide-react";

const IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/pnnuoixs_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_57_18%20PM.png";

export default function TestingCertification() {
  useSEO({
    title: "Fire Safety Testing & Certification Ireland | FSS",
    description:
      "Scheduled fire alarm testing, emergency lighting testing, fire extinguisher servicing, maintenance reports and clear service documentation across Ireland.",
    canonicalPath: "/services/testing-certification",
  });

  return (
    <>
      <PageHero
        eyebrow="Testing & Certification"
        title="Fire Safety Testing & Certification"
        subtitle="Regular inspection and testing help ensure that fire safety systems remain reliable, operational and appropriately documented."
        image={IMG}
        imageAlt="Fire alarm call-point inspected during routine testing"
        ctaPrimary={{ to: "/contact", label: "Request a Quote" }}
        ctaSecondary={{ to: "/services/maintenance", label: "Maintenance Services" }}
      />

      <ServiceBody
        includedSubtitle="Our testing & certification services"
        included={[
          { icon: BellRing, title: "Fire alarm testing", copy: "Scheduled testing of fire detection and alarm systems." },
          { icon: Lightbulb, title: "Emergency lighting testing", copy: "Routine testing of emergency lighting fixtures and control gear." },
          { icon: Flame, title: "Fire extinguisher servicing", copy: "Annual extinguisher servicing carried out on site." },
          { icon: FileText, title: "Maintenance reports", copy: "Reports detailing the work carried out at each visit." },
          { icon: ClipboardCheck, title: "Service records", copy: "Clear service records so you can maintain your fire safety file." },
          { icon: FileCheck2, title: "Documentation", copy: "Documentation issued following completed servicing and testing." },
        ]}
        whoFor={[
          "Businesses maintaining scheduled fire safety testing programmes",
          "Property managers coordinating multi-site inspection cycles",
          "Premises transitioning from ad-hoc to structured servicing",
          "Contractors handing over completed fire safety installations",
        ]}
        benefits={[
          "A structured testing schedule that fits your premises",
          "Clear information following each service visit",
          "Improved visibility of the ongoing status of your systems",
          "Support for your wider fire safety documentation",
        ]}
        defaultService="Testing & Certification"
      />

      <section className="bg-slate-50 border-y border-slate-200 fss-section">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="fss-overline">Records &amp; documentation</div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Clear Service Records and Documentation
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-slate-600 leading-relaxed">
            <p>
              Following scheduled testing and servicing, Fire Safety Systems
              provides clear service information and documentation relating to
              the work completed.
            </p>
            <p>
              This documentation supports your ongoing fire safety
              record-keeping. Please note that servicing and inspection alone do
              not confirm the overall legal compliance of a building — the
              wider fire safety strategy for your premises should be reviewed
              accordingly.
            </p>
          </div>
        </div>
      </section>

      <FAQBlock
        eyebrow="Testing & certification FAQ"
        title="Frequently asked questions"
        items={[
          { q: "What is included in a testing visit?", a: "Testing typically includes functional testing of the installed equipment, inspection of the system, identification of any faults and reporting on the work carried out." },
          { q: "What documentation do we receive?", a: "Following the visit we provide clear service information covering the equipment tested, faults identified and any recommended remedial actions." },
          { q: "Does servicing confirm my building is legally compliant?", a: "No — servicing and inspection are important, but overall compliance depends on the wider fire safety strategy for your premises. We recommend obtaining advice appropriate to your circumstances." },
          { q: "Can testing be coordinated across multiple sites?", a: "Yes — we can operate a planned schedule with site-by-site reporting and central records for the portfolio." },
          { q: "What if the visit identifies faults?", a: "Faults are reported to you with clear recommendations. Where possible we can complete minor repairs during the same visit; larger works are quoted separately." },
          { q: "How often should testing be carried out?", a: "The appropriate frequency depends on the systems installed and the requirements of your premises. We will agree an appropriate schedule with you." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Keep your fire safety systems tested and properly documented."
        subtitle="Contact FSS to discuss a scheduled testing programme."
        primary={{ to: "/contact", label: "Request a Quote" }}
        secondary={{ to: "/contact", label: "Discuss a Testing Programme" }}
      />
    </>
  );
}
